import CreditCardIcon from "@/assets/all-icons/credit-card-icon.jsx";
import DeleteIcon from "@/assets/all-icons/delete-icon.jsx";
import useTranslations from "@/i18n/useTranslations";
import Loader from '@/shared-components/loader/Loader';
import Text from "@/shared-components/text/components/Text";

import "./styles/PaymentMethodCard.css";

function formatExpiration(expMonth, expYear, fallbackValue) {
  if (!expMonth || !expYear) {
    return fallbackValue;
  }
  const month = String(expMonth).padStart(2, "0");
  const year = String(expYear).slice(-2);
  return `${month}/${year}`;
}

export default function PaymentMethodCard({
  method,
  onSetDefault,
  onDelete,
  isUpdating = false,
  isDeleting = false,
}) {
  const t = useTranslations('common.user_settings.payment_methods.card');

  if (!method) {
    return null;
  }

  const maskedDigits = method.last4 ? `**** ${method.last4}` : t('card_on_file');
  const fundingLabel = method.funding ? `${method.funding} ${t('funding_suffix')}` : null;
  const expiration = formatExpiration(method.expMonth, method.expYear, t('unknown_value'));
  const isSelectable = !method.isDefault && !isUpdating && !isDeleting;

  const selectMethod = () => {
    if (isSelectable) {
      onSetDefault?.(method);
    }
  };

  const handleKeyDown = (event) => {
    if (!isSelectable || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    selectMethod();
  };

  return (
    <div
      className={`payment-method-card${method.isDefault ? " default" : ""}${isSelectable ? " selectable" : ""}`}
      onClick={selectMethod}
      aria-busy={isUpdating || undefined}
    >
      <div
        className="payment-method-card__body"
        role={isSelectable ? "button" : undefined}
        tabIndex={isSelectable ? 0 : undefined}
        onKeyDown={handleKeyDown}
        aria-label={isSelectable ? t('set_default_button') : undefined}
      >
        <div className="payment-method-card__icon">
          <CreditCardIcon />
        </div>

        <div className="payment-method-card__details">
          <div className="payment-method-card__title-row">
            <Text className="payment-method-card__brand font-size-medium">
              {method.label ?? method.brand ?? t('payment_method_fallback')}
            </Text>
            {fundingLabel && (
              <Text className="payment-method-card__funding font-size-small">
                {fundingLabel}
              </Text>
            )}
          </div>

          <Text className="payment-method-card__number font-size-medium" paragraph>
            {maskedDigits}
          </Text>

          <div className="payment-method-card__meta">
            <Text className="payment-method-card__meta-item font-size-small">
              {t('expires_label')} {expiration}
            </Text>
            {method.country && (
              <Text className="payment-method-card__meta-item font-size-small">
                {method.country}
              </Text>
            )}
          </div>
        </div>
      </div>
      <div className="payment-method-card__actions">
        {method.isDefault && (
          <span className="payment-method-card__badge">{t('default_badge')}</span>
        )}

        <button
          type="button"
          className="payment-method-card__delete-action"
          onClick={(event) => {
            event.stopPropagation();
            onDelete?.(method);
          }}
          disabled={isDeleting || isUpdating}
          aria-label={t('remove_button')}
          title={t('remove_button')}
        >
          {isDeleting ? (
            <Loader inline className="payment-method-card__spinner" thin />
          ) : (
            <DeleteIcon className="payment-method-card__delete-icon" />
          )}
        </button>
      </div>
    </div>
  );
}
